/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";

type Group = {
  supplier_id: number | null;
  supplier_name: string | null;
  email: string | null;
  estimated_total: number | null;
  items: {
    stock_item_id: number;
    name: string;
    base_unit: string;
    on_hand: number;
    par_level: number;
    unit: string;
    quantity: number;
    estimated_cost: number | null;
  }[];
};

type Props = { onClose: () => void };

// What to reorder to get back to par, per supplier, ready to send.
// See docs/plans/inventory_and_costing/09_insight_and_ordering.md (#6).
export const ShoppingListModal = defineComponent<Props>({
  name: "ShoppingListModal",
  props: ["onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const router = useRouter();
    const loading = ref(true);
    const creating = ref<number | null>(null);
    const groups = ref<Group[]>([]);

    onMounted(async () => {
      try {
        groups.value = (await trpc.stock.shoppingList.query()) as Group[];
      } catch {
        toast.showToast(t("inventory.stock.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    const orderText = (g: Group) =>
      [
        t("inventory.shopping.order_heading").replace("{supplier}", g.supplier_name ?? ""),
        ...g.items.map((i) => `- ${i.quantity} × ${i.unit} ${i.name}`),
      ].join("\n");

    const copy = async (g: Group) => {
      try {
        await navigator.clipboard.writeText(orderText(g));
        toast.showToast(t("inventory.shopping.copied"), { type: "success" });
      } catch {
        toast.showToast(t("inventory.shopping.copy_failed"), { type: "error" });
      }
    };

    // A draft purchase order with the suggested quantities at last prices,
    // opened on the Orders page to review and send.
    const createOrder = async (g: Group) => {
      creating.value = g.supplier_id;
      try {
        const order = await trpc.purchaseOrders.save.mutate({
          supplier_id: g.supplier_id,
          lines: g.items.map((i) => ({
            stock_item_id: i.stock_item_id,
            quantity: i.quantity,
            unit: i.unit,
            unit_price: i.estimated_cost != null ? Math.round((i.estimated_cost / i.quantity) * 10000) / 10000 : 0,
          })),
        });
        props.onClose();
        router.push({ path: "/app/inventory/orders", query: { open: String(order.id) } });
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("inventory.orders.save_failed"), { type: "error" });
      } finally {
        creating.value = null;
      }
    };

    const mailto = (g: Group) =>
      `mailto:${encodeURIComponent(g.email ?? "")}?subject=${encodeURIComponent(
        t("inventory.shopping.email_subject"),
      )}&body=${encodeURIComponent(orderText(g))}`;

    return () => (
      <LemmonModal
        title={t("inventory.shopping.title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4 text-right">
            <button class="btn btn-neutral" onClick={() => props.onClose()}>
              {t("inventory.purchases.close")}
            </button>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          {loading.value ? (
            <div class="flex justify-center p-6">
              <Loading />
            </div>
          ) : groups.value.length === 0 ? (
            <p class="text-sm text-base-content/60">{t("inventory.shopping.empty")}</p>
          ) : (
            groups.value.map((g) => (
              <div key={g.supplier_id ?? "none"} class="rounded-lg border border-base-300">
                <div class="flex items-center justify-between border-b border-base-300 px-4 py-2">
                  <div>
                    <div class="font-semibold">{g.supplier_name ?? t("inventory.shopping.no_supplier")}</div>
                    {g.estimated_total != null && (
                      <div class="text-xs text-base-content/60">
                        {t("inventory.shopping.estimated").replace("{total}", g.estimated_total.toFixed(2))}
                      </div>
                    )}
                  </div>
                  {g.supplier_id != null && (
                    <div class="flex gap-1">
                      <button
                        class="btn btn-primary btn-xs"
                        disabled={creating.value != null}
                        onClick={() => createOrder(g)}
                      >
                        <span class="material-symbols-outlined text-sm">add_shopping_cart</span>
                        {t("inventory.shopping.create_order")}
                      </button>
                      <button class="btn btn-ghost btn-xs" onClick={() => copy(g)}>
                        <span class="material-symbols-outlined text-sm">content_copy</span>
                        {t("inventory.shopping.copy")}
                      </button>
                      {g.email && (
                        <a class="btn btn-ghost btn-xs" href={mailto(g)}>
                          <span class="material-symbols-outlined text-sm">mail</span>
                          {t("inventory.shopping.email")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <table class="table table-sm">
                  <tbody>
                    {g.items.map((i) => (
                      <tr key={i.stock_item_id}>
                        <td>{i.name}</td>
                        <td class="text-xs text-base-content/60">
                          {t("inventory.shopping.on_hand_par")
                            .replace("{on_hand}", `${Number(i.on_hand.toFixed(2))}`)
                            .replace("{par}", `${Number(i.par_level.toFixed(2))}`)
                            .replace(/\{unit\}/g, i.base_unit)}
                        </td>
                        <td class="text-right font-medium">{`${i.quantity} × ${i.unit}`}</td>
                        <td class="text-right text-sm">
                          {i.estimated_cost == null ? "—" : i.estimated_cost.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      </LemmonModal>
    );
  },
});
